/**
 * /beta?invite=<token> — public landing for redeeming a beta invite.
 *
 * Flow:
 *   1. Page mounts, reads ?invite=<token> from URL.
 *   2. POSTs to /api/beta/redeem.
 *   3. On success, drops the returned JWT into localStorage and bounces to
 *      /dashboard (where the existing auth flow takes over).
 *   4. On failure (expired / revoked / invalid), shows a friendly error
 *      with a contact link.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, ArrowRight, AlertCircle, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BetaRedeem = () => {
  const [params] = useSearchParams();
  const token = params.get('invite');
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', message: '' });

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', message: 'No invite token in URL.' });
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/beta/redeem`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          setState({ status: 'error', message: j.detail || `Invite invalid (${res.status})` });
          return;
        }
        const data = await res.json();
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('is_beta', '1');
        setState({ status: 'ready', message: '' });
        setTimeout(() => navigate('/dashboard'), 1100);
      } catch (e) {
        setState({ status: 'error', message: 'Network error redeeming invite.' });
      }
    })();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4" data-testid="beta-redeem-page">
      <Card className="bg-slate-900/60 border-slate-800 max-w-md w-full">
        <CardContent className="p-8 text-center">
          {state.status === 'loading' && (
            <>
              <Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white">Activating your beta access…</h1>
              <p className="text-sm text-slate-400 mt-2">Setting up your sandbox + seed data.</p>
            </>
          )}
          {state.status === 'ready' && (
            <>
              <div className="w-12 h-12 rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/40 mx-auto mb-4 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <Badge className="mb-3 bg-emerald-500/10 text-emerald-400 border-emerald-500/20 px-3 py-1 text-xs font-semibold">Welcome to Beta</Badge>
              <h1 className="text-2xl font-bold text-white">You're in. Sandbox ready.</h1>
              <p className="text-sm text-slate-400 mt-2">Redirecting to your dashboard…</p>
            </>
          )}
          {state.status === 'error' && (
            <>
              <AlertCircle className="w-10 h-10 text-rose-400 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white">Invite couldn't be activated</h1>
              <p className="text-sm text-slate-400 mt-3" data-testid="beta-error-msg">{state.message}</p>
              <div className="flex flex-col gap-2 mt-6">
                <Link to="/contact">
                  <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold w-full" data-testid="beta-contact-btn">
                    Request a new invite <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/" className="text-xs text-slate-400 hover:text-slate-200 mt-1">← Back to home</Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BetaRedeem;
